import React, { useEffect, useContext, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Box, Button, Divider, CircularProgress, Alert, IconButton, Tooltip } from '@mui/material';
import { IconArrowLeft } from '@tabler/icons-react';
import { Refresh } from '@mui/icons-material';
import { ChatProvider, ChatContext } from '@/context/ChatContext';
import AppCard from '@/components/shared/AppCard.tsx';
import ChatContent from '@/components/chats/ChatContent.tsx';
import ChatMsgSent from '@/components/chats/ChatMsgSent.tsx';
import { useTranslation } from '@/hooks/useTranslation';
import { ApiTicketCustomer } from '@/types/apps/ticket';

interface LocationState {
  subject?: string;
  status?: string;
  supportType?: string;
  customer?: ApiTicketCustomer | null;
}

const TicketDetailsContent: React.FC<{
  ticketId: string;
  ticketInfo?: LocationState;
  refreshKey: number;
}> = ({ ticketId, ticketInfo, refreshKey }) => {
  const { loadTicketMessages, loading, error } = useContext(ChatContext);

  useEffect(() => {
    if (ticketId) {
      loadTicketMessages(ticketId, ticketInfo);
    }
  }, [ticketId, refreshKey]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <AppCard>
      <Box flexGrow={1}>
        <ChatContent />
        <Divider />
        <ChatMsgSent ticketId={ticketId} />
      </Box>
    </AppCard>
  );
};

const TicketDetailsPage: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const ticketInfo = location.state as LocationState | undefined;
  const { t } = useTranslation();
  const [refreshKey, setRefreshKey] = useState(0);

  if (!ticketId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert variant="filled" severity="error">{t('ticketDetails.errors.ticketIdMissing')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button
          startIcon={<IconArrowLeft size="20" />}
          onClick={() => navigate('/tickets')}
        >
          {t('ticketDetails.backToTickets')}
        </Button>
        <Tooltip title={t('createdFileService.refreshMessages')}>
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setRefreshKey((k) => k + 1)}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <ChatProvider>
        <TicketDetailsContent ticketId={ticketId} ticketInfo={ticketInfo} refreshKey={refreshKey} />
      </ChatProvider>
    </Box>
  );
};

export default TicketDetailsPage;

